import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

/* Every notification for the signed-in user, newest first, with the action
   each one needs. Client messages and stuck reports land here; the Clients
   page only links in. */

interface AlertRow {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  link: string;
  report_uuid: string | null;
  client_account: string | null;
  client_account_name: string | null;
  is_read: boolean;
  created_at: string;
}

const TYPE_LABEL: Record<string, string> = {
  report_ready: 'Report ready',
  contact_request: 'Message',
  member_request: 'Add member',
  report_request: 'Report request',
  report_requested: 'Report requested',
  admin_message: 'Alert',
  dispute_created: 'Dispute',
  dispute_closed: 'Dispute',
};

export default function AlertsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser;
  const client = searchParams.get('client') || '';

  const [rows, setRows] = useState<AlertRow[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const params: Record<string, string> = { page_size: '100' };
      if (client) params.client = client;
      const res = await api.get('/notifications/', { params });
      setRows(res.data.results ?? res.data);
    } catch {
      setError('Could not load alerts.');
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/accounts/').then((res) => setAccounts(res.data)).catch(() => {});
  }, [isAdmin]);

  const markRead = async (id: number) => {
    setError('');
    try {
      await api.post(`/notifications/${id}/mark-as-read/`);
      setRows((cur) => cur.map((r) => (r.id === id ? { ...r, is_read: true } : r)));
    } catch {
      setError('Could not update the alert.');
    }
  };

  const markAllRead = async () => {
    setError('');
    try {
      // With a client filter on, only that client's alerts are marked.
      await api.post('/notifications/mark-all-as-read/', null, { params: client ? { client } : {} });
      setRows((cur) => cur.map((r) => ({ ...r, is_read: true })));
    } catch {
      setError('Could not update the alerts.');
    }
  };

  const remove = async (id: number) => {
    setError('');
    try {
      await api.delete(`/notifications/${id}/`);
      setRows((cur) => cur.filter((r) => r.id !== id));
    } catch {
      setError('Could not delete the alert.');
    }
  };

  // A message or request opens the client's card; anything else opens its link.
  const open = (r: AlertRow) => navigate(r.client_account ? `/clients#acct-${r.client_account}` : r.link);

  const requeue = async (r: AlertRow) => {
    if (!r.report_uuid) return;
    setError('');
    try {
      await api.post(`/reports/${r.report_uuid}/requeue/`);
      setMessage('Report requeued.');
      markRead(r.id);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not requeue.');
    }
  };

  const unread = rows.filter((r) => !r.is_read).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: '#102B3D' }}>Alerts</Typography>
        {isAdmin && (
          <TextField
            select
            size="small"
            value={client}
            onChange={(e) => setSearchParams(e.target.value ? { client: e.target.value } : {})}
            sx={{ width: 220 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All clients</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </TextField>
        )}
        {unread > 0 && (
          <Button size="small" onClick={markAllRead} sx={{ ml: 'auto' }}>Mark all read</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper variant="outlined" square sx={{ borderColor: '#A9B8C4' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#EAF1F5' }}>
              <TableCell sx={{ width: 110 }}>Date</TableCell>
              <TableCell sx={{ width: 130 }}>Type</TableCell>
              {isAdmin && <TableCell sx={{ width: 180 }}>Client</TableCell>}
              <TableCell>Alert</TableCell>
              <TableCell align="right" sx={{ width: 220 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4}>
                  <Typography variant="body2" color="text.secondary">No alerts.</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} sx={{ bgcolor: r.is_read ? undefined : '#FFF8E6', '& td': { verticalAlign: 'top' } }}>
                <TableCell>{r.created_at.slice(0, 10)}</TableCell>
                <TableCell>{TYPE_LABEL[r.notification_type] || r.notification_type}</TableCell>
                {isAdmin && <TableCell>{r.client_account_name || ''}</TableCell>}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: r.is_read ? 400 : 600 }}>{r.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{r.message}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {!r.is_read && r.report_uuid && r.title.startsWith('Report stuck') && (
                    <Button size="small" onClick={() => requeue(r)}>Requeue</Button>
                  )}
                  {(r.client_account || r.link) && (
                    <Button size="small" onClick={() => open(r)}>Open</Button>
                  )}
                  {!r.is_read && (
                    <Button size="small" onClick={() => markRead(r.id)}>Done</Button>
                  )}
                  <Button size="small" color="error" onClick={() => remove(r.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
